import {
  createLightTheme,
  createDarkTheme,
  webLightTheme,
  webDarkTheme,
  type BrandVariants,
  type Theme,
} from '@fluentui/react-components';
import { APP_BASE_FONT_SIZE_PX, APP_FONT_FAMILY, fontScale } from './scalingTokens';

export const gemBrandColors: BrandVariants = {
  10: '#001526',
  20: '#00243F',
  30: '#003559',
  40: '#004578',
  50: '#005A9E',
  60: '#0062AC',
  70: '#006CBB',
  80: '#0078D4',
  90: '#2B88D8',
  100: '#559BD5',
  110: '#7AAFDA',
  120: '#9FC3E0',
  130: '#B4D6FA',
  140: '#D0E7F8',
  150: '#E8F3FC',
  160: '#FFFFFF',
};

// GEM light/dark themes are the standard Fluent web themes with ONLY the brand
// ramp (and brand-derived tokens) overridden. `createLightTheme(brand)` already
// returns a complete theme whose key set is identical to `webLightTheme` (which
// is itself `createLightTheme(webBrandVariants)`), so spreading the web theme
// first is value-identical for every token the generator emits — a deliberate
// visual no-op. Stating the `webLightTheme`/`webDarkTheme` base explicitly makes
// the inheritance obvious and defensively backfills any token a future Fluent
// release might add to the web theme but not yet wire through the generator.
//
// Typography tokens are additionally re-expressed in `rem` (keyed off the
// 15-px desktop base) so that Fluent components (Button, Input, Dropdown, …)
// scale with the html `font-size: clamp(…)` set by `AppStyles`, instead of
// staying pinned at their native px sizes on mobile.
const px = (value: number): string => `${value / APP_BASE_FONT_SIZE_PX}rem`;

const remTypography = {
  fontSizeBase100: fontScale.caption,
  fontSizeBase200: fontScale.body,
  fontSizeBase300: fontScale.body,
  fontSizeBase400: fontScale.body,
  fontSizeBase500: px(20),
  fontSizeBase600: px(24),
  fontSizeHero700: px(28),
  fontSizeHero800: px(32),
  fontSizeHero900: px(40),
  fontSizeHero1000: px(68),
  lineHeightBase100: px(14),
  lineHeightBase200: px(16),
  lineHeightBase300: px(20),
  lineHeightBase400: px(22),
  lineHeightBase500: px(28),
  lineHeightBase600: px(32),
  lineHeightHero700: px(36),
  lineHeightHero800: px(40),
  lineHeightHero900: px(52),
  lineHeightHero1000: px(92),
} as const;

// Force every Fluent font-family token onto the single app font stack so that
// components reading `tokens.fontFamilyBase` (Button, Input, Text, …) match the
// `APP_FONT_FAMILY` applied to `html`/`body` and the FluentProvider root. The
// monospace/numeric variants are aligned too so nothing falls back to Fluent's
// default Segoe stack.
const fontFamilies = {
  fontFamilyBase: APP_FONT_FAMILY,
  fontFamilyMonospace: APP_FONT_FAMILY,
  fontFamilyNumeric: APP_FONT_FAMILY,
} as const;

export const gemLightTheme: Theme = {
  ...webLightTheme,
  ...createLightTheme(gemBrandColors),
  ...remTypography,
  ...fontFamilies,
};

export const gemDarkTheme: Theme = {
  ...webDarkTheme,
  ...createDarkTheme(gemBrandColors),
  ...remTypography,
  ...fontFamilies,
};

export const bannerGradientEnd = '#C4EDFF';

// Left navigation rail sizing and animation tokens (spec 095-left-nav-bar).
// Widths are expressed as CSS length strings so they can be consumed directly
// by `makeStyles` and CSS custom properties. The expanded width is sized to
// snugly fit the longest rendered label (incl. fr-CA sub-items such as
// "Liste des événements") without leaving excess horizontal whitespace.
export const railWidthExpanded = '12rem';
export const railWidthCollapsed = '3rem';
export const railTransitionDuration = '250ms';
export const railTransitionEasing = 'cubic-bezier(0.33, 0, 0.67, 1)';

// Footer chrome height (spec 097-footer-help-revamp). Defined in rem so the
// fixed-position footer height stays in lockstep with the existing header's
// 3rem chrome and the App layout's paddingBottom reservation.
export const FOOTER_HEIGHT = '3rem';

// Vertical clearance reserved at the bottom of a scroll area when a
// FloatingActionBar is pinned above the footer (e.g., the home "book another"
// banner and the Available Spaces Back/Save/Next bar). Sized to cover the bar's
// own height plus a small gap so the last in-flow content can scroll just clear
// of the bar without leaving excessive empty space (the footer offset itself is
// already reserved by the App layout's paddingBottom).
export const FLOATING_ACTION_BAR_CLEARANCE = '4.5rem';

export type ThemeMode = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'gem-theme-mode';

export function getStoredThemeMode(): ThemeMode | null {
  try {
    const stored = sessionStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }
    return null;
  } catch {
    return null;
  }
}

export function setStoredThemeMode(mode: ThemeMode): void {
  try {
    sessionStorage.setItem(THEME_STORAGE_KEY, mode);
  } catch {
    console.warn('Unable to persist theme preference to session storage');
  }
}

export function getThemeForMode(mode: ThemeMode): Theme {
  return mode === 'dark' ? gemDarkTheme : gemLightTheme;
}
